export const DELIVERED_ITEM_ADJUSTMENT_TYPES = [
  "quantity_correction",
  "quantity_returned",
  "quantity_added",
  "description_correction",
  "uom_correction",
  "serial_returned",
  "serial_correction",
] as const;
export type DeliveredItemAdjustmentType = (typeof DELIVERED_ITEM_ADJUSTMENT_TYPES)[number];

export type DeliveredItemLineSnapshot = {
  quantity: number;
  uom: string | null;
  description: string;
  serials: string[];
};

export type DeliveredItemAdjustmentInput = {
  type: DeliveredItemAdjustmentType;
  before: DeliveredItemLineSnapshot;
  requested: {
    quantityDelta?: number;
    resultingQuantity?: number;
    description?: string;
    uom?: string | null;
    serial?: string | null;
    replacementSerial?: string | null;
  };
  reason: string;
  occurrenceKey: string;
};

export type DeliveredItemAdjustmentDecision =
  | { ok: true; after: DeliveredItemLineSnapshot }
  | { ok: false; code: string; message: string };

function roundQty(value: number): number {
  return Math.round(value * 1000) / 1000;
}

export function normalizeDeliveredSerial(raw: string | null | undefined): string {
  return String(raw ?? "")
    .trim()
    .toUpperCase()
    .replace(/[\s\-_.]/g, "");
}

export function applyDeliveredItemAdjustment(
  input: DeliveredItemAdjustmentInput,
): DeliveredItemAdjustmentDecision {
  const reason = String(input.reason ?? "").trim();
  if (!reason) {
    return { ok: false, code: "reason_required", message: "A reason is required for every adjustment." };
  }
  if (!String(input.occurrenceKey ?? "").trim()) {
    return { ok: false, code: "occurrence_required", message: "An idempotency key is required." };
  }

  const before = {
    quantity: Number(input.before.quantity),
    uom: input.before.uom ?? null,
    description: String(input.before.description ?? ""),
    serials: [...input.before.serials],
  };
  if (!Number.isFinite(before.quantity)) {
    return { ok: false, code: "invalid_before", message: "The original quantity is not valid." };
  }

  if (input.type === "quantity_correction" || input.type === "quantity_returned" || input.type === "quantity_added") {
    const hasResulting = input.requested.resultingQuantity !== undefined && input.requested.resultingQuantity !== null;
    const hasDelta = input.requested.quantityDelta !== undefined && input.requested.quantityDelta !== null;
    if (!hasResulting && !hasDelta) {
      return {
        ok: false,
        code: "quantity_required",
        message: "Enter the proposed quantity. An empty quantity cannot be recorded as zero.",
      };
    }
    const resulting = hasResulting
      ? Number(input.requested.resultingQuantity)
      : before.quantity + Number(input.requested.quantityDelta);
    if (!Number.isFinite(resulting)) {
      return { ok: false, code: "invalid_quantity", message: "The requested quantity is not valid." };
    }
    if (resulting < 0) {
      return { ok: false, code: "negative_quantity", message: "Resulting quantity cannot be negative." };
    }
    if (input.type === "quantity_returned" && resulting >= before.quantity) {
      return {
        ok: false,
        code: "must_reduce",
        message: "A return/removal must reduce the current quantity.",
      };
    }
    if (input.type === "quantity_added" && resulting <= before.quantity) {
      return {
        ok: false,
        code: "must_increase",
        message: "A quantity addition must increase the current quantity.",
      };
    }
    if (before.serials.length > 0 && Math.round(resulting) !== before.serials.length) {
      return {
        ok: false,
        code: "serial_quantity_mismatch",
        message: "Serialized quantity must match the current serial count. Return the exact serial instead.",
      };
    }
    return { ok: true, after: { ...before, quantity: roundQty(resulting) } };
  }

  if (input.type === "description_correction") {
    const description = String(input.requested.description ?? "").trim();
    if (!description) {
      return { ok: false, code: "description_required", message: "A customer-facing description is required." };
    }
    return { ok: true, after: { ...before, description } };
  }

  if (input.type === "uom_correction") {
    const uom = String(input.requested.uom ?? "").trim() || null;
    return { ok: true, after: { ...before, uom } };
  }

  if (input.type === "serial_returned") {
    const serial = normalizeDeliveredSerial(input.requested.serial);
    if (!serial) {
      return { ok: false, code: "serial_required", message: "Select the exact serial number being returned." };
    }
    const idx = before.serials.findIndex((value) => normalizeDeliveredSerial(value) === serial);
    if (idx < 0) {
      return { ok: false, code: "unknown_serial", message: "That serial is not currently on this delivered line." };
    }
    const serials = before.serials.filter((_, i) => i !== idx);
    return { ok: true, after: { ...before, serials, quantity: roundQty(Math.max(0, before.quantity - 1)) } };
  }

  if (input.type === "serial_correction") {
    return {
      ok: false,
      code: "serial_correction_unavailable",
      message:
        "Serial number correction is unavailable until the warranty serial identity can be updated safely.",
    };
  }

  return { ok: false, code: "unsupported_adjustment", message: "This adjustment type is not supported." };
}

export function projectCurrentDeliveredQuantity(
  originalQuantity: number,
  adjustments: ReadonlyArray<{ type: DeliveredItemAdjustmentType; afterQuantity: number }>,
): number {
  if (adjustments.length === 0) return Math.max(0, roundQty(originalQuantity));
  return Math.max(0, roundQty(adjustments[adjustments.length - 1]!.afterQuantity));
}

export function canMutateDeliveredItems(role: string | null | undefined): boolean {
  const r = String(role ?? "").toLowerCase();
  return r === "admin" || r === "procurement" || r === "inventory";
}

export function canViewOriginalDo(role: string | null | undefined): boolean {
  const r = String(role ?? "").toLowerCase();
  return r === "admin" || r === "finance" || r === "inventory" || r === "procurement";
}

export const DELIVERED_ITEM_ADJUSTMENT_LABELS: Record<DeliveredItemAdjustmentType, string> = {
  quantity_correction: "Correct quantity",
  quantity_returned: "Record returned/removed quantity",
  quantity_added: "Add quantity through correction",
  description_correction: "Correct customer-facing description",
  uom_correction: "Correct UOM",
  serial_returned: "Return/remove exact serialized unit",
  serial_correction: "Correct exact serial number",
};

export const CLARITY_ONE_WARRANTY_FORBIDDEN_LIFECYCLES = [
  "pending",
  "creating",
  "draft_created",
  "failed_retryable",
  "review_required",
  "superseded",
  "voided",
] as const;

export const CLARITY_ONE_WARRANTY_PATH = "/warranty";

export function customerSafeWarrantyLink(label: "Warranty live" | "Warranty being prepared" | null): string | null {
  return label === "Warranty live" ? CLARITY_ONE_WARRANTY_PATH : null;
}

export function staffWarrantyCardHref(cardId: number | null | undefined): string | null {
  return cardId != null && cardId > 0 ? `/warranties/${cardId}` : null;
}

export function customerSafeWarrantyLabel(input: {
  cardStatus?: string | null;
  handoffLifecycle?: string | null;
}): "Warranty live" | "Warranty being prepared" | null {
  const card = String(input.cardStatus ?? "").toLowerCase();
  if (card === "issued") return "Warranty live";
  if (card === "draft" || card === "pending_approval" || card === "changes_requested" || card === "on_hold") {
    return "Warranty being prepared";
  }
  const life = String(input.handoffLifecycle ?? "").toLowerCase();
  if (life === "pending" || life === "creating" || life === "draft_created") {
    return "Warranty being prepared";
  }
  return null;
}

export function assertNoInternalWarrantyLifecycle(payload: unknown): string[] {
  const json = JSON.stringify(payload ?? {});
  return CLARITY_ONE_WARRANTY_FORBIDDEN_LIFECYCLES.filter((token) =>
    new RegExp(`"${token}"`, "i").test(json),
  );
}

export function canonicalizeAdjustmentRequest(requested: Record<string, unknown>): string {
  const clean: Record<string, unknown> = {};
  for (const key of Object.keys(requested).sort()) {
    const value = requested[key];
    if (value !== undefined) clean[key] = value;
  }
  return JSON.stringify(clean);
}

export function occurrenceReplayMatches(input: {
  existing: {
    deliveryId: number;
    deliveryLineId: number;
    customerId: number;
    customerSiteId: number;
    adjustmentType: string;
    requestedDeltaJson: unknown;
  };
  incoming: {
    deliveryId: number;
    deliveryLineId: number;
    customerId: number;
    customerSiteId: number;
    type: string;
    requested: Record<string, unknown>;
  };
}): boolean {
  if (input.existing.deliveryId !== input.incoming.deliveryId) return false;
  if (input.existing.deliveryLineId !== input.incoming.deliveryLineId) return false;
  if (input.existing.customerId !== input.incoming.customerId) return false;
  if (input.existing.customerSiteId !== input.incoming.customerSiteId) return false;
  if (input.existing.adjustmentType !== input.incoming.type) return false;
  const existingReq =
    input.existing.requestedDeltaJson && typeof input.existing.requestedDeltaJson === "object"
      ? (input.existing.requestedDeltaJson as Record<string, unknown>)
      : {};
  return canonicalizeAdjustmentRequest(existingReq) === canonicalizeAdjustmentRequest(input.incoming.requested);
}

export const CLARITY_ONE_DELIVERY_FORBIDDEN_KEYS = [
  "storageKey",
  "staffNote",
  "extractionMethod",
  "extractionWarnings",
  "uploader",
  "uploadedBy",
  "actorId",
  "reason",
  "landedCost",
  "cost",
  "margin",
  "poNumber",
  "supplier",
  "audit",
  "rawDo",
] as const;

export function assertCustomerSafeDeliveryPayload(payload: unknown): string[] {
  const json = JSON.stringify(payload ?? {});
  const leaks = CLARITY_ONE_DELIVERY_FORBIDDEN_KEYS.filter((key) =>
    new RegExp(`"${key}"\\s*:`, "i").test(json),
  );
  if (/\/warranties\/\d+/i.test(json)) leaks.push("staffWarrantyRoute");
  return leaks;
}

export const CUSTOMER_DELIVERY_PDF_ALLOWED_FIELDS = [
  "customerName",
  "siteAddress",
  "deliveryDate",
  "description",
  "quantity",
  "uom",
  "serials",
  "generatedAt",
  "notice",
] as const;
